import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core'
import { useLayoutEffect, useRef, useState } from 'react'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  MapFilterConfigItemDto,
  MapFilterState,
  sortMapFiltersByPosition,
} from '@ivy-monorepo/shared'
import Icon from '../Icon'

type MapFiltersTableProps = {
  items: MapFilterConfigItemDto[]
  onChange: (items: MapFilterConfigItemDto[]) => void
  onUpdateItem: (key: MapFilterConfigItemDto['key'], patch: Partial<MapFilterConfigItemDto>) => void
}

type SortableRowProps = {
  item: MapFilterConfigItemDto
  position: number
  onUpdateItem: (key: MapFilterConfigItemDto['key'], patch: Partial<MapFilterConfigItemDto>) => void
}

type MapFilterRowContentProps = {
  item: MapFilterConfigItemDto
  position: number
  onUpdateItem?: (key: MapFilterConfigItemDto['key'], patch: Partial<MapFilterConfigItemDto>) => void
  dragHandleProps?: Record<string, unknown>
  isOverlay?: boolean
}

const MapFilterRowContent = ({
  item,
  position,
  onUpdateItem,
  dragHandleProps,
  isOverlay = false,
}: MapFilterRowContentProps) => {
  const readOnly = isOverlay || !onUpdateItem

  return (
    <>
      <td className="map-filter-drag-cell">
        <button
          type="button"
          className="map-filter-drag-handle"
          aria-label={`Drag to reorder ${item.label}`}
          disabled={readOnly}
          {...dragHandleProps}
        >
          <Icon name="GripVertical" className="map-filter-drag-icon" />
          <span className="map-filter-position-label">{position}</span>
        </button>
      </td>
      <td>
        <code>{item.key}</code>
      </td>
      <td>
        <input
          type="text"
          className="map-filter-input"
          value={item.label}
          readOnly={readOnly}
          onChange={e => onUpdateItem?.(item.key, { label: e.target.value })}
        />
      </td>
      <td>
        <input
          type="color"
          className="map-filter-color"
          value={item.color}
          readOnly={readOnly}
          onChange={e => onUpdateItem?.(item.key, { color: e.target.value })}
        />
        <input
          type="text"
          className="map-filter-input map-filter-hex"
          value={item.color}
          readOnly={readOnly}
          onChange={e => onUpdateItem?.(item.key, { color: e.target.value })}
        />
      </td>
      <td>
        <select
          className="map-filter-select"
          value={item.state}
          disabled={readOnly}
          onChange={e => onUpdateItem?.(item.key, { state: e.target.value as MapFilterState })}
        >
          {Object.values(MapFilterState).map(state => (
            <option key={state} value={state}>
              {state}
            </option>
          ))}
        </select>
      </td>
      <td>
        <input
          type="text"
          className="map-filter-input map-filter-payload"
          placeholder="null"
          value={item.payload == null ? '' : JSON.stringify(item.payload)}
          readOnly={readOnly}
          onChange={e => {
            const raw = e.target.value.trim()
            if (raw.length === 0) {
              onUpdateItem?.(item.key, { payload: null })
              return
            }
            try {
              const parsed = JSON.parse(raw) as Record<string, unknown>
              if (parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)) {
                onUpdateItem?.(item.key, { payload: parsed })
              }
            } catch {
              // keep typing until valid JSON
            }
          }}
        />
      </td>
    </>
  )
}

const SortableMapFilterRow = ({ item, position, onUpdateItem }: SortableRowProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.key })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={isDragging ? 'map-filter-row-dragging' : undefined}
    >
      <MapFilterRowContent
        item={item}
        position={position}
        onUpdateItem={onUpdateItem}
        dragHandleProps={{ ...attributes, ...listeners }}
      />
    </tr>
  )
}

const MapFiltersTable = ({ items, onChange, onUpdateItem }: MapFiltersTableProps) => {
  const tableRef = useRef<HTMLTableElement>(null)
  const [tableWidth, setTableWidth] = useState<number>()
  const sortedItems = sortMapFiltersByPosition(items)
  const sortableIds = sortedItems.map(item => item.key)
  const [activeKey, setActiveKey] = useState<MapFilterConfigItemDto['key'] | null>(null)

  useLayoutEffect(() => {
    if (!tableRef.current) {
      return
    }

    const updateWidth = () => setTableWidth(tableRef.current?.offsetWidth)
    updateWidth()

    const observer = new ResizeObserver(updateWidth)
    observer.observe(tableRef.current)
    return () => observer.disconnect()
  }, [sortedItems.length])

  const activeItem = activeKey == null ? null : sortedItems.find(item => item.key === activeKey) ?? null
  const activePosition = activeItem == null ? -1 : sortedItems.findIndex(item => item.key === activeKey)

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const handleDragStart = (event: DragStartEvent) => {
    setActiveKey(event.active.id as MapFilterConfigItemDto['key'])
  }

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveKey(null)

    const { active, over } = event
    if (!over || active.id === over.id) {
      return
    }

    const oldIndex = sortedItems.findIndex(item => item.key === active.id)
    const newIndex = sortedItems.findIndex(item => item.key === over.id)
    if (oldIndex < 0 || newIndex < 0) {
      return
    }

    const reordered = arrayMove(sortedItems, oldIndex, newIndex).map((item, position) => ({
      ...item,
      position,
    }))
    onChange(reordered)
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setActiveKey(null)}
    >
      <table ref={tableRef} className="map-filters-table">
        <thead>
          <tr>
            <th className="map-filter-drag-header">Order</th>
            <th>Key</th>
            <th>Label</th>
            <th>Color</th>
            <th>State</th>
            <th>Payload (JSON)</th>
          </tr>
        </thead>
        <tbody>
          <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
            {sortedItems.map((item, index) => (
              <SortableMapFilterRow
                key={item.key}
                item={item}
                position={index}
                onUpdateItem={onUpdateItem}
              />
            ))}
          </SortableContext>
        </tbody>
      </table>
      <DragOverlay dropAnimation={null}>
        {activeItem ? (
          <table
            className="map-filters-table map-filters-table-overlay"
            style={tableWidth ? { width: tableWidth } : undefined}
          >
            <tbody>
              <tr className="map-filter-row-overlay">
                <MapFilterRowContent item={activeItem} position={activePosition} isOverlay />
              </tr>
            </tbody>
          </table>
        ) : null}
      </DragOverlay>
    </DndContext>
  )
}

export default MapFiltersTable
